import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  AlertTriangle,
  Base,
  ErrorIcon,
  ReverLoading,
  SuccessIcon,
  TruckIcon2
} from '@itsrever/design-system'
import { useReportIncident } from '@/application/use-report-incident'
import IncidentTextBox from './incident-text-box'
import ReverIncidentSelect from './rever-incident-select'
import LogisticsIncidentSelect from './logistics-incident-select'
import IncidentFilePicker from './incident-file-picker'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'

const {
  Button,
  DialogFooter,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogClose,
  DialogCloseButton,
  ToggleGroup,
  ToggleGroupItem
} = Base

type Incident = 'logistics' | 'rever'

const DEFAULT_INCIDENT_TYPE: Incident = 'logistics'

interface IncidentModalProps {
  isOpen: boolean
  onRequestClose: () => void
  processId: string
}

const IncidentModal: React.FC<IncidentModalProps> = ({
  isOpen,
  onRequestClose,
  processId
}) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) onRequestClose()
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogBackdrop />
        {/* With an invoice and five photos, a short or zoomed screen would push Report off it. */}
        <DialogPopup className="max-h-[calc(100vh-2rem)] overflow-y-auto">
          {isOpen ? (
            <IncidentForm processId={processId} onClose={onRequestClose} />
          ) : null}
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}

interface IncidentFormProps {
  processId: string
  onClose: () => void
}

const IncidentForm = ({ processId, onClose }: IncidentFormProps) => {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const [searchParams] = useSearchParams()

  const [incidentType, setIncidentType] = useState<Incident>(
    DEFAULT_INCIDENT_TYPE
  )
  const [issueTitle, setIssueTitle] = useState<string | undefined>(undefined)
  const [translatedIssueText, setTranslatedIssueText] = useState<string>('')
  const [incidentDescription, setIncidentDescription] = useState('')
  const [invoice, setInvoice] = useState<File[]>([])
  const [photos, setPhotos] = useState<File[]>([])

  const user = useAppSelector((store) => store.userApi.getMe.response.user)
  const ecommerceId =
    useAppSelector((s) => s.generalData.selectedEcommerce) ?? ''

  const {
    reportIncidentRequest,
    loadingIncidentReport,
    incidentReportSuccess,
    incidentReportError
  } = useReportIncident()

  const needsInvoice = incidentType === 'logistics'
  const needsPhotos = needsInvoice && issueTitle === 'damaged_package'

  const showDescriptionValidationError = incidentDescription.length < 5
  const sendButtonDisabled =
    showDescriptionValidationError ||
    issueTitle === undefined ||
    (needsInvoice && invoice.length === 0) ||
    (needsPhotos && photos.length === 0)

  const handleSubmit = () => {
    if (sendButtonDisabled) return

    reportIncidentRequest({
      group: user?.group ?? '',
      ecommerce: searchParams.get('ecommerce_id') ?? ecommerceId,
      user: user?.name ?? '',
      processId: processId,
      incidentType: incidentType,
      issueTitle: issueTitle ?? '',
      translatedIssueTitle: translatedIssueText,
      incidentDescription: incidentDescription,
      language: language ?? 'en',
      invoice: needsInvoice ? invoice[0] : undefined,
      photos: needsPhotos ? photos : []
    })
  }

  useEffect(() => {
    if (incidentReportSuccess || incidentReportError) {
      const timer = setTimeout(onClose, 2000)
      return () => clearTimeout(timer)
    }
  }, [incidentReportSuccess, incidentReportError, onClose])

  const onChangeIssue = (reasonId: string, reasonLabel: string) => {
    setIssueTitle(reasonId ?? '')
    setTranslatedIssueText(t(reasonLabel))
  }

  return (
    <div className="flex w-[600px] flex-col">
      <DialogCloseButton />
      {(loadingIncidentReport ||
        incidentReportSuccess ||
        incidentReportError) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          {loadingIncidentReport && <ReverLoading />}
          {incidentReportSuccess && <SuccessIcon className="size-12" />}
          {incidentReportError && (
            <div className="flex flex-col items-center justify-center gap-2">
              <ErrorIcon className="size-12" />
              <p className="text-base">
                Sorry, there was an error reporting the incident. Please try
                again later.
              </p>
            </div>
          )}
        </div>
      )}
      <ToggleGroup
        variant="button"
        aria-label={t('process_details_page.report_incident.modal_title')}
        className="mt-2"
        value={[incidentType]}
        // Pressing the chosen type again reports no value; the choice stays.
        onValueChange={(next) => {
          if (next.length) setIncidentType(next[0] as Incident)
        }}
      >
        <ToggleGroupItem
          value="logistics"
          className="inline-flex items-center gap-1.5"
        >
          <TruckIcon2 />
          {t('process_details_page.report_incident.logistics_label')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="rever"
          className="inline-flex items-center gap-1.5"
        >
          <AlertTriangle className="size-4" />
          {t('process_details_page.report_incident.rever_label')}
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="mt-8 px-2">
        {incidentType === 'rever' ? (
          <ReverIncidentSelect
            onReasonChanged={(reasonId, label) =>
              onChangeIssue(reasonId ?? '', label ?? '')
            }
          />
        ) : (
          <LogisticsIncidentSelect
            onReasonChanged={(reasonId, label) =>
              onChangeIssue(reasonId ?? '', label ?? '')
            }
          />
        )}
      </div>
      <div>
        <IncidentTextBox
          description={incidentDescription}
          onDescriptionChange={(value) => setIncidentDescription(value)}
          showValidationError={showDescriptionValidationError}
        />
      </div>
      {needsInvoice && (
        <IncidentFilePicker
          label={t('process_details_page.report_incident.invoice_label')}
          description={t('process_details_page.report_incident.invoice_hint')}
          buttonLabel={t('process_details_page.report_incident.choose_file')}
          files={invoice}
          onChange={setInvoice}
        />
      )}
      {needsPhotos && (
        <IncidentFilePicker
          label={t('process_details_page.report_incident.photos_label')}
          description={t('process_details_page.report_incident.photos_hint')}
          buttonLabel={t('process_details_page.report_incident.add_photos')}
          multiple
          files={photos}
          onChange={setPhotos}
        />
      )}

      <DialogFooter className="mt-10">
        <DialogClose render={<Button variant="secondary" type="button" />}>
          {t('process_details_page.report_incident.cancel_button')}
        </DialogClose>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={sendButtonDisabled}
        >
          {t('process_details_page.report_incident.report_button')}
        </Button>
      </DialogFooter>
    </div>
  )
}

export default IncidentModal
